import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { BalanceAvailabilitiesParams } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/utils";

export class FeeAnalysisSpecialProductNames {
  static readonly CFS_Edge = "CFS Edge";
  static readonly Expand = "Expand";
  static readonly Essential = "Essential";
  static readonly North = "North";

  static readonly VariableMap = new Map<string, AnalysisVariable[]>()
    .set(this.CFS_Edge, [AnalysisVariable.Edge_eligible_MF_balance])
    .set(this.North, [AnalysisVariable.NorthSelect_MF_balance]);

  static readonly AdditionalVariableProductMap = new Map<
    string,
    AnalysisVariable[]
  >()
    .set(this.CFS_Edge, [AnalysisVariable.NorthSelect_MF_balance])
    .set(this.North, [AnalysisVariable.Edge_eligible_MF_balance]);

  static readonly ProductsToHideSetupNames: string[] = [
    this.CFS_Edge,
    this.North,
  ];
}

export class FeeAnalysisBalanceVariableNames {
  static readonly IDPS_Cash_balance = AnalysisVariable.IDPS_Cash_balance;
  static readonly Super_Cash_balance = AnalysisVariable.Super_Cash_balance;
  static readonly MF_balance_i = AnalysisVariable.MF_balance_i;
  static readonly Edge_eligible_MF_balance =
    AnalysisVariable.Edge_eligible_MF_balance;
  static readonly NorthSelect_MF_balance =
    AnalysisVariable.NorthSelect_MF_balance;
  static readonly TD_balance_i = AnalysisVariable.TD_balance_i;
  static readonly Intl_balance_i = AnalysisVariable.Intl_balance_i;
  static readonly AU_balance_i = AnalysisVariable.AU_balance_i;
  static readonly Bond_balance_i = AnalysisVariable.Bond_balance_i;

  static readonly CashBalances = [
    this.IDPS_Cash_balance,
    this.Super_Cash_balance,
  ];
  static readonly ManagedFundsBalances = [
    this.MF_balance_i,
    this.Edge_eligible_MF_balance,
    this.NorthSelect_MF_balance,
  ];
  static readonly TermDepositsBalances = [this.TD_balance_i];
  static readonly InternationalBalances = [this.Intl_balance_i];
  static readonly AustralianSharesBalances = [this.AU_balance_i];
  static readonly BondBalances = [this.Bond_balance_i];

  static readonly BalanceAvailabilities = [
    ...this.CashBalances,
    ...this.TermDepositsBalances,
    ...this.ManagedFundsBalances,
    ...this.InternationalBalances,
    ...this.AustralianSharesBalances,
    ...this.BondBalances,
  ];
}

export class PortfolioSetupConstants {
  static readonly DefaultDelayTimeMs = 300;
  static readonly MinimumDelayTimeMs = 50;
  static readonly MaximumPortfolio = 40;
  static readonly MaximumSelectedInvestments = 50;
  static readonly DefaultDecimalPlaces = 2;
  static readonly DefaultPrecision = 11;
  static readonly TooltipPrecision = 5;
  static readonly MinimumWeightToSave = 0.01;
  static readonly NegativeZeroString = "-0.00";
  static readonly MaximumPortfolioWarning =
    "Portfolio limit reached. Click Next, Back or Exit to clear the unused portfolios.";
  static readonly TooltipZeroOverflow = "0.00000...";
  static readonly BelowMinimumInvestmentSizeWarning =
    "Allocations below minimum investment size";
  static readonly AllocationMustSumTo100 = "Allocation must sum to 100%";
  static readonly MissingInvestmentOption = "Missing investment option";
  static readonly NoIDPSBalanceIncluded =
    "No IDPS accounts included in the analysis";
  static readonly NoSuperBalanceIncluded =
    "No Super/Pension accounts included in the analysis";
  static readonly MissingInvestment = "Missing investment";
  static readonly NotAvailableInIDPS = "Not available in IDPS";
  static readonly NotAvailableInSuper = "Not available in Super/Pension";
  static readonly NotAvailableInIDPSAndSuper =
    "Not available in IDPS and Super";
  static readonly InvestmentOptionNotAvailable =
    "Investment option not available in IDPS/Super";
  static readonly InvestmentNotAvailable =
    "Investment not available in IDPS/Super";
  static readonly CashNotAvailableInSuperError =
    "Cash in Super/Pension accounts is not available";
}

export const VariableNameToBalanceMap: Partial<
  Record<
    AnalysisVariable,
    {
      availableIDPSKey: keyof BalanceAvailabilitiesParams;
      availableSuperKey: keyof BalanceAvailabilitiesParams;
    }
  >
> = {
  [AnalysisVariable.TD_balance_i]: {
    availableIDPSKey: "termDepositsAvailableInIDPS",
    availableSuperKey: "termDepositsAvailableInSuper",
  },
  [AnalysisVariable.MF_balance_i]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.Edge_eligible_MF_balance]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.NorthSelect_MF_balance]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.Intl_balance_i]: {
    availableIDPSKey: "intlListedSecuritiesAvailableInIDPS",
    availableSuperKey: "intlListedSecuritiesAvailableInSuper",
  },
  [AnalysisVariable.AU_balance_i]: {
    availableIDPSKey: "auListedSecuritiesAvailableInIDPS",
    availableSuperKey: "auListedSecuritiesAvailableInSuper",
  },
  [AnalysisVariable.Bond_balance_i]: {
    availableIDPSKey: "fixedIncomeAvailableInIDPS",
    availableSuperKey: "fixedIncomeAvailableInSuper",
  },
  [AnalysisVariable.Super_Cash_balance]: {
    availableIDPSKey: "cashAvailableInIDPS",
    availableSuperKey: "cashAvailableInSuper",
  },
};
